// src/pages/OnboardingExisting.js
import React, { useState } from "react";
import TextInput from "../components/TextInput";
import FileUploader from "../components/FileUploader";
import SignaturePad from "../components/SignaturePad";
import TermsModal from "../components/TermsModal";
// Import the API function for submission
import { submitOnboardingExisting } from "../services/api";

function OnboardingExisting() {
  const [formData, setFormData] = useState({
    // Required text fields
    first_name: "",
    last_name: "",
    email1: "",
    date_of_birth: "",
    address_line_1: "",
    address_line_2: "",
    address_city: "",
    postcode: "",
    county: "",
    phone_number: "",
    occupation: "",
    working_hours: "",
    payment_full_name: "",
    payment_sort_code: "",
    payment_account_number: "",
    referral1: "",
    user_code: "",

    // Checkbox fields
    high_demand_events: false,
    buying_more_tickets: false,
    transfer_service: false,
    allow_tt_signups: false,
    all_tt_manage_passwords: false,
    allow_tt_create_emails: false,
    live_rebate_updates: false,

    // Terms acceptance
    terms_accepted: false,

    // File & Signature
    file: null,
    signature_base64: null,
  });

  // State to control the Terms modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  // State to prevent duplicate submissions
  const [isSubmitted, setIsSubmitted] = useState(false);
  // State for the success message
  const [submissionMessage, setSubmissionMessage] = useState("");

  // Handle text and checkbox changes
  const handleChange = (e) => {
    console.debug("[OnboardingExisting] handleChange =>", e.target.name, e.target.value);
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle file selection from the FileUploader component
  const handleFileSelected = (file) => {
    console.debug("[OnboardingExisting] File selected =>", file);
    setFormData((prev) => ({ ...prev, file }));
  };

  // Capture the signature (base64 string) from the SignaturePad component
  const handleSignatureChange = (signatureData) => {
    console.debug("[OnboardingExisting] Signature changed => base64 length:", signatureData?.length || 0);
    setFormData((prev) => ({ ...prev, signature_base64: signatureData }));
  };

  // Modal controls for Terms & Conditions
  const openModal = (e) => {
    e.preventDefault();
    console.debug("[OnboardingExisting] Opening Terms & Conditions modal.");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    console.debug("[OnboardingExisting] Closing Terms & Conditions modal.");
    setIsModalOpen(false);
  };

  const acceptTerms = () => {
    console.debug("[OnboardingExisting] Terms accepted by user.");
    setFormData((prev) => ({ ...prev, terms_accepted: true }));
  };

  // Submit handler with validation and duplicate prevention
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.debug("[OnboardingExisting] Form submission initiated.");

    // If form has been submitted already, prevent further submission
    if (isSubmitted) {
      console.warn("[OnboardingExisting] Form already submitted. Aborting new submission.");
      alert("Your form has already been submitted.");
      return;
    }

    // List of required text fields (adjust as needed)
    const requiredTextFields = [
      "first_name",
      "last_name",
      "email1",
      "date_of_birth",
      "address_line_1",
      "address_line_2",
      "address_city",
      "postcode",
      "county",
      "phone_number",
      "occupation",
      "working_hours",
      "payment_full_name",
      "payment_sort_code",
      "payment_account_number",
      "user_code",
    ];

    for (const fieldName of requiredTextFields) {
      if (!formData[fieldName].trim()) {
        console.warn(`[OnboardingExisting] Missing required field => ${fieldName}`);
        alert(`Please fill out the "${fieldName}" field.`);
        return;
      }
    }

    // Check file and signature presence
    if (!formData.file) {
      console.warn("[OnboardingExisting] Missing ID document (file).");
      alert("Please upload the required ID document.");
      return;
    }
    if (!formData.signature_base64) {
      console.warn("[OnboardingExisting] Missing signature.");
      alert("Please provide your signature.");
      return;
    }

    // Check Terms & Conditions acceptance
    if (!formData.terms_accepted) {
      console.warn("[OnboardingExisting] Terms & Conditions not accepted.");
      alert("You must accept the Terms & Conditions to proceed.");
      return;
    }

    console.log("[OnboardingExisting] Submitting form data:", formData);

    try {
      // Call the API to submit the form
      const result = await submitOnboardingExisting(formData);
      console.debug("[OnboardingExisting] API response =>", result);
      const successMsg = `Your onboarding form was successfully submitted with ID ${result.id}. A member of the team will be in touch shortly to confirm your details and documents.`;
      setSubmissionMessage(successMsg);
      setIsSubmitted(true);
      alert(successMsg);
    } catch (error) {
      console.error("[OnboardingExisting] Submission error:", error);
      alert("Submission failed. Please try again or contact support.");
    }
  };

  return (
    <div style={{ maxWidth: "700px", margin: "0 auto" }}>
      <h1>Onboarding - Existing Customer</h1>
      {isSubmitted ? (
        <div style={{ marginBottom: "1rem", color: "green" }}>
          {submissionMessage}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* ---- Basic Information ---- */}
          <div style={{ marginBottom: "1rem" }}>
            <strong>Basic Information</strong>
          </div>
          <TextInput
            label="First Name (required)"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
          />
          <TextInput
            label="Last Name (required)"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
          />
          <TextInput
            label="Primary Email (required)"
            name="email1"
            value={formData.email1}
            onChange={handleChange}
            type="email"
          />
          <TextInput
            label="Date of Birth (required)"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            type="date"
          />
          <TextInput
            label="Address Line 1 (required)"
            name="address_line_1"
            value={formData.address_line_1}
            onChange={handleChange}
          />
          <TextInput
            label="Address Line 2 (required)"
            name="address_line_2"
            value={formData.address_line_2}
            onChange={handleChange}
          />
          <TextInput
            label="City (required)"
            name="address_city"
            value={formData.address_city}
            onChange={handleChange}
          />
          <TextInput
            label="Postcode (required)"
            name="postcode"
            value={formData.postcode}
            onChange={handleChange}
          />
          <TextInput
            label="County (required)"
            name="county"
            value={formData.county}
            onChange={handleChange}
          />
          <TextInput
            label="Phone Number (required)"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            type="tel"
          />
          <TextInput
            label="Current Occupation/Job (required)"
            name="occupation"
            value={formData.occupation}
            onChange={handleChange}
          />
          <TextInput
            label="Working Hours (required)"
            name="working_hours"
            value={formData.working_hours}
            onChange={handleChange}
          />

          {/* ---- User Permissions and Notifications ---- */}
          <div style={{ margin: "1rem 0 0.5rem" }}>
            <strong>User Permissions and Notifications</strong>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label>
              <input
                type="checkbox"
                name="high_demand_events"
                checked={formData.high_demand_events}
                onChange={handleChange}
              />
              &nbsp;<strong>Receive Advice on High-Demand Events</strong>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;I agree to receive advice and notifications from TT regarding high-demand events.
            </label>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label>
              <input
                type="checkbox"
                name="buying_more_tickets"
                checked={formData.buying_more_tickets}
                onChange={handleChange}
              />
              &nbsp;<strong>Receive Advice on Buying More Tickets Within Promoter Limits</strong>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;I agree to receive guidance from TT on how to purchase additional tickets while complying with promoter limits.
            </label>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label>
              <input
                type="checkbox"
                name="transfer_service"
                checked={formData.transfer_service}
                onChange={handleChange}
              />
              &nbsp;<strong>Transfer Service: Allow TT to Deliver Tickets to Customers</strong>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;I consent to TT International handling the delivery of tickets to customers on my behalf.
            </label>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label>
              <input
                type="checkbox"
                name="allow_tt_signups"
                checked={formData.allow_tt_signups}
                onChange={handleChange}
              />
              &nbsp;<strong>Allow TT to Complete Sign-Ups on My Behalf</strong>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;I authorize TT International to complete ticketing sign-ups on my behalf.
            </label>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label>
              <input
                type="checkbox"
                name="all_tt_manage_passwords"
                checked={formData.all_tt_manage_passwords}
                onChange={handleChange}
              />
              &nbsp;<strong>Allow TT to Manage Passwords for Ticket Sites</strong>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;I consent to TT International managing my login credentials for ticket sites.
            </label>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label>
              <input
                type="checkbox"
                name="allow_tt_create_emails"
                checked={formData.allow_tt_create_emails}
                onChange={handleChange}
              />
              &nbsp;<strong>Allow TT to Create Additional Emails on My Behalf</strong>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;I authorize TT International to create additional email addresses for my use.
            </label>
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label>
              <input
                type="checkbox"
                name="live_rebate_updates"
                checked={formData.live_rebate_updates}
                onChange={handleChange}
              />
              &nbsp;<strong>Receive Live Updates on Rebates</strong>
              <br />
              &nbsp;&nbsp;&nbsp;&nbsp;I agree to receive live notifications regarding my rebate status.
            </label>
          </div>

          {/* ---- Payment Details ---- */}
          <div style={{ margin: "1rem 0 0.5rem" }}>
            <strong>Payment Details</strong>
          </div>
          <TextInput
            label="Payment Full Name (required)"
            name="payment_full_name"
            value={formData.payment_full_name}
            onChange={handleChange}
          />
          <TextInput
            label="Payment Sort Code (required)"
            name="payment_sort_code"
            value={formData.payment_sort_code}
            onChange={handleChange}
          />
          <TextInput
            label="Payment Account Number (required)"
            name="payment_account_number"
            value={formData.payment_account_number}
            onChange={handleChange}
          />

          {/* ---- Additional Details ---- */}
          <div style={{ margin: "1rem 0 0.5rem" }}>
            <strong>Additional Details</strong>
          </div>
          <TextInput
            label="Referral 1"
            name="referral1"
            value={formData.referral1}
            onChange={handleChange}
          />
          <TextInput
            label="User Code (assigned by admin) (required)"
            name="user_code"
            value={formData.user_code}
            onChange={handleChange}
          />

          {/* ---- Verification ---- */}
          <div style={{ margin: "1rem 0 0.5rem" }}>
            <strong>Verification</strong>
          </div>
          <FileUploader
            label="Upload ID Document (required)"
            onFileSelected={handleFileSelected}
          />
          <SignaturePad
            label="Your Signature (required)"
            onSignatureChange={handleSignatureChange}
          />

          {/* ---- Terms & Conditions Acceptance ---- */}
          <div style={{ margin: "1rem 0 1rem" }}>
            <label>
              <input
                type="checkbox"
                name="terms_accepted"
                checked={formData.terms_accepted}
                onChange={() => {}}
                disabled
              />
              &nbsp;I have read and accept the Terms & Conditions (required)
            </label>
            &nbsp;
            <button onClick={openModal} style={{ marginLeft: "6px" }}>
              View T&C & Privacy
            </button>
          </div>

          <button type="submit" disabled={isSubmitted}>
            Submit Form
          </button>
        </form>
      )}
      {isModalOpen && (
        <TermsModal onClose={closeModal} onAccept={acceptTerms} />
      )}
    </div>
  );
}

export default OnboardingExisting;
